import { ACTION_SEARCH_AVAILABILITY } from 'utils/analytics/eventTypeConstantsAmplitude';
import { PatronLocationStateContext } from 'components/patron-location/PatronLocationContext';
import {
  SearchMode,
  SearchQueryStringParams,
  buildQueryStringParams,
} from 'components/search/utils';
import {
  SearchRequestParams,
  buildSearchRequestParams,
} from 'utils/search/buildSearchRequestParams';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { amplitudeSearchResultsEvent, getAnalyticsEventType } from 'utils/analytics/amplitude';
import { clearSsrInitialization } from 'actions/AvailabilityActions';
import { consumer } from '@tock/coretypes';
import { getAvailability } from 'selectors/availabilitySearchSelectors';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useContext } from 'react';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useDebouncedCallback } from 'use-debounce';
import { useDeepMemo } from 'hooks/useDeepMemo';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useQueryStringParams } from 'hooks/useQueryStringParams';
import { useRoutedCollectionPageInfo } from 'selectors/consumerPageSelectors';
import useDeepCompareEffect from 'use-deep-compare-effect';

/**
 * Dispatches a search request when the searchQuery changes.
 * @param {SearchMode} mode - the mode the search is in
 * @returns {StoreType['availability']} availability - availability state
 */
export function useSearchRequest(mode?: SearchMode): {
  availability: StoreType['availability'];
  searchQuery?: SearchRequestParams;
} {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentMetroArea = useCurrentMetroArea();
  const queryStringParams = useQueryStringParams<SearchQueryStringParams>();
  const { initialized: patronLocationInitialized, currentPatronLocation } = useContext(
    PatronLocationStateContext
  );
  const availability = useSelector(getAvailability);
  const {
    actions: {
      availability: { search },
      toast: { showError },
    },
  } = useActions();
  const amplitudeSendEvent = useAmplitudeSendEvent();

  const {
    id: collectionId,
    collectionRoute,
    dateStart,
    dateEnd,
    offeringSearchType: collectionOfferingSearchType,
    configMode,
  } = useRoutedCollectionPageInfo();

  // TODO: ATM when you click book now on search, for togo experiences it changes the query string param to size of 1
  // this causes a new search, and when the dialog is closed another search. Future changes can mitigate this by either
  // changing the url structure or changing the logic around what causes a change in search query
  // such as maybe more specific parameters
  // for now we are returning undefined if show modal is present, but this still
  // searches when the dialog is closed
  const searchQuery = useDeepMemo<SearchRequestParams | undefined>(() => {
    // wait for init of patron location history
    if (!patronLocationInitialized) {
      return undefined;
    }

    // when showing a modal on search, return undefined as to not exeute a new search
    if (queryStringParams.showModal) {
      return undefined;
    }

    const filledInQueryStringParams = mode
      ? buildQueryStringParams({
          queryStringParams,
          mode,
          patronLatLng: currentPatronLocation
            ? { lat: currentPatronLocation.lat, lng: currentPatronLocation.lng }
            : undefined,
        })
      : {};

    const params = mode
      ? buildSearchRequestParams(
          mode.stateType,
          filledInQueryStringParams,
          currentMetroArea?.timezone as string,
          {
            collectionId,
            collectionDate: dateStart,
            collectionDateEnd: dateEnd,
            collectionOfferingSearchType,
          }
        )
      : undefined;

    return params;
  }, [
    currentMetroArea?.timezone,
    currentPatronLocation,
    mode,
    patronLocationInitialized,
    queryStringParams,
  ]);

  const executeSearch = useDebouncedCallback((searchQuery: SearchRequestParams) => {
    // If showing a collection page, check that the collectionId is valid. Also ensure that
    // a collectionId only persists if search mode is "collectionPage" (this prevents against a consumer
    // page and collection being added at an existing search URL)
    let collectionIdToDispatch = collectionId;
    if (mode?.stateType === 'collectionPage' && !collectionId) {
      history.replace('/');
      showError(`No Collection found at /${collectionRoute}`);
    } else if (mode?.stateType !== 'collectionPage') {
      collectionIdToDispatch = undefined;
    }

    if (searchQuery.query.type === 'EVENT') {
      // This is defensive code to ensure we're never sending up a party size
      // for Event search. This guards against difficult-to-solve edge cases
      // where users swap between Event and non-Event search and the party size
      // hangs around in the URL querystring.
      searchQuery.query.size = undefined;
    }

    // collectionIdToDispatch is being passed with the data config id, but read as the named configuration id
    // Update this logic to use configMode to set the correct parameter `dataConfigurationId` or `collectionDataConfigurationId` so that it can be interepreted by server
    search(searchQuery.query, {
      enableCache: false,
      additionalQuery: searchQuery.additionalQuery,
      collectionId: configMode === 'NAMED_CONFIGURATION' ? collectionIdToDispatch : undefined,
      collectionDataConfigurationId:
        configMode === 'COLLECTION_DATA' ? collectionIdToDispatch : undefined,
    }).then((response: ProtoResponse<consumer.BusinessAvailabilitySearchResponse>) => {
      amplitudeSendEvent(
        ACTION_SEARCH_AVAILABILITY,
        amplitudeSearchResultsEvent(
          searchQuery,
          getAnalyticsEventType(mode),
          response.payload.count
        )
      );
    });
  }, 200);

  useDeepCompareEffect(() => {
    if (!searchQuery) {
      return;
    } else if (availability.wasJustInitializedFromSSR) {
      // we're skipping client side initialization becacuse of ssr initialization
      dispatch(clearSsrInitialization());
      return;
    }
    executeSearch(searchQuery);
  }, [dispatch, searchQuery]);

  return { availability, searchQuery };
}
